import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import { errorMessage } from '../api/client';
import { useCreateUser, useUpdateUser, useUsers } from '../api/hooks';
import type { Role, User } from '../types';
import { dateTime } from '../utils/format';

const ASSIGNABLE: Role[] = ['Admin', 'Accountant', 'Viewer'];

const ROLE_COLOR: Record<string, string> = {
  Admin: 'red',
  Accountant: 'blue',
  Viewer: 'default',
  Investor: 'green',
};

export default function UsersPage() {
  const { data, isLoading } = useUsers();
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Staff users
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          Add user
        </Button>
      </Space>

      <Card>
        <Table<User>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Username', dataIndex: 'username' },
            { title: 'Full name', dataIndex: 'fullName' },
            {
              title: 'Role',
              dataIndex: 'role',
              render: (value: string) => <Tag color={ROLE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            {
              title: 'Active',
              dataIndex: 'isActive',
              render: (value: boolean) => <Switch checked={value} disabled size="small" />,
            },
            { title: 'Created', dataIndex: 'createdAt', render: dateTime },
            {
              title: 'Actions',
              render: (_, row) => (
                <Button type="link" size="small" onClick={() => setEditing(row)}>
                  Edit
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <CreateUserModal open={creating} onClose={() => setCreating(false)} />
      <EditUserModal user={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const create = useCreateUser();

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await create.mutateAsync(values);
      message.success('User created.');
      form.resetFields();
      onClose();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal
      title="Add staff user"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={create.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} initialValues={{ role: 'Viewer' }}>
        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true },
            { min: 3 },
            { pattern: /^[A-Za-z0-9._-]+$/, message: 'Letters, digits, dot, underscore and hyphen only.' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="password" label="Password" rules={[{ required: true }, { min: 8 }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select options={ASSIGNABLE.map((r) => ({ value: r, label: r }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function EditUserModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const [form] = Form.useForm();
  const update = useUpdateUser();

  const onFinish = async (values: Record<string, unknown>) => {
    if (!user) return;
    try {
      await update.mutateAsync({ id: user.id, ...values });
      message.success('User updated.');
      onClose();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal
      title={`Edit ${user?.username ?? ''}`}
      open={!!user}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={update.isPending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        preserve={false}
        initialValues={user ? { fullName: user.fullName, role: user.role, isActive: user.isActive } : undefined}
      >
        <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select options={ASSIGNABLE.map((r) => ({ value: r, label: r }))} />
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="password" label="New password (leave blank to keep)" rules={[{ min: 8 }]}>
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
}
